import React, { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ReactComponent as PlayIcon } from "../../assets/Icons/play.svg";
import { ReactComponent as HeartIcon } from "../../assets/Icons/heart.svg";
import { ReactComponent as HeartFillIcon } from "../../assets/Icons/heartFill.svg";
import { ReactComponent as PlayingIcon } from "../../assets/Icons/playingIcon.svg";
import Videos from "../Shorts/Player";
import Comments_container from "../../Components/Comments/Components/Comments_container";
import InsertCommentIcon from '@mui/icons-material/InsertComment';
import ShareIcon from '@mui/icons-material/Share';
import { toast, ToastContainer } from 'react-toastify';
import Loading from "../../Components/Loading/Loading";
import BundlesList from "../../Components/Bundles/Bundles";
import LockIcon from "../../assets/Images/lock.png";
import Translate from "../../Components/Multilanguage/Translate";
import { ref, onValue, off } from 'firebase/database';
import { imageUrlCheck, onVideoPlayFunction } from "../../utils/utils";
import * as service from "./service";
import { getUserDetails } from "../../Redux/UserDetailsSlice/UserDetailsSlice";
import { fetchComments } from "../../Components/Comments/service";

let debounce = true;
var videoStarted = false;
let seconds = 60;
let prevTime;
let videoPlaying = false;

const VerticalShowPlayer = () => {
    const dispatch = useDispatch();
    const projectInfo = useSelector((state) => state?.projectInfo?.value);
    const sessionId = useSelector((state) => state?.sessionId?.value);
    const accessToken = useSelector((state) => state?.accessToken?.value);
    const user = useSelector((state) => state?.user?.value);
    const userDetails = useSelector((state) => state?.userDetails?.value);
    const userCoins = useSelector((state) => state?.userDetails?.value?.user_coins);
    const unlockedEpisodesRef = useRef(new Set());
    const recordedTransactionsRef = useRef(new Set());
    const autoProcessedBundleRef = useRef(new Set());
    const resumePaymentHandledRef = useRef(false);

    const [loading, setLoading] = useState(true);
    const [showData, setShowData] = useState(null);
    const [episodes, setEpisodes] = useState([]);
    const [currentEpisodeIndex, setCurrentEpisodeIndex] = useState(0);
    const [currentEpisode, setCurrentEpisode] = useState(null);
    const [likedEpisodes, setLikedEpisodes] = useState(new Set());
    const [isCommentModalOpen, setCommentModalOpen] = useState(false);
    const [shouldCloseCommentModal, setShouldCloseCommentModal] = useState(false);
    const [commentCount, setCommentCount] = useState(0);
    const [currentEpisodeRange, setCurrentEpisodeRange] = useState({ start: 0, end: 49 });
    const [episodeRanges, setEpisodeRanges] = useState([]);
    const [nextShowId, setNextShowId] = useState(null);
    const [showBundlesModal, setShowBundlesModal] = useState(false);
    const [requiredTokenId, setRequiredTokenId] = useState(null);
    const [requiredEpisodePrice, setRequiredEpisodePrice] = useState(null);
    const [totalComments, setTotalComments] = useState(0);

    const location = useLocation();
    const navigate = useNavigate();
    const { showIdentifier: paramShowId, episodeIdentifier: urlEpisodeId } = useParams();
    const playerRef = useRef(null);

    const episodeIdFromUrl = urlEpisodeId;
    const episodeId = episodeIdFromUrl;

    const appInfo = useMemo(() => ({
        projectDetails: projectInfo,
        accessToken: accessToken,
        sessionId,
    }), [projectInfo, accessToken, sessionId]);
    let sortType = "latest"

    useEffect(() => {
        const showIdentifier = paramShowId || location?.state?.showId || location?.state?.showVanity;
        if (showIdentifier) {
            fetchVerticalShowData(showIdentifier);
        } else {
            navigate(-1);
        }
    }, [paramShowId]);

    useEffect(() => {
        if (episodes?.length > 0 && currentEpisodeIndex >= 0) {
            setCurrentEpisode(episodes[currentEpisodeIndex]);
            
            const ep = episodes[currentEpisodeIndex];
            const episodeIdentifier = ep?.vanity_url || ep?.video_id;
            const showIdentifier = showData?.vanity_url || paramShowId;
            if (episodeIdentifier) {
                const newUrl = `/vertical-show/${showIdentifier}/${episodeIdentifier}`;
                if (location.pathname !== newUrl) {
                    navigate(newUrl, { replace: true, state: location.state });
                }
            }
        }
    }, [episodes, currentEpisodeIndex, paramShowId]);

    useEffect(() => {
        if (episodes?.length > 0) {
            const ranges = [];
            for (let i = 0; i < episodes.length; i += 50) {
                const end = Math.min(i + 49, episodes.length - 1);
                ranges.push({ start: i, end: end });
            }
            setEpisodeRanges(ranges);

            const currentRange = ranges.find(range =>
                currentEpisodeIndex >= range.start && currentEpisodeIndex <= range.end
            );
            if (currentRange) {
                setCurrentEpisodeRange(currentRange);
            }
        }
    }, [episodes, currentEpisodeIndex]);

    useEffect(() => {
        if (!currentEpisode || currentEpisode?.subscribed !== false) {
            return;
        }

        if (location?.state?.resumeFromBundlePurchase) {
            return;
        }

        const bundleOptions = getPaymentOptions(currentEpisode).filter((option) => option?.type === 'bundle');
        if (bundleOptions.length !== 1) {
            return;
        }

        const singleBundle = bundleOptions[0];
        const autoProcessKey = `${currentEpisode?.video_id}_${singleBundle?.consumption_type}`;

        if (autoProcessedBundleRef.current.has(autoProcessKey)) {
            return;
        }

        // DIRECT BALANCE CHECK: No token ID matching required
        if (Number(userCoins?.current_balance || 0) >= Number(singleBundle?.tokens_required || 0)) {
            autoProcessedBundleRef.current.add(autoProcessKey);
            handleCoinPayment(currentEpisode, singleBundle);
        }
    }, [currentEpisode?.video_id, currentEpisode?.subscribed, userCoins, location?.state?.resumeFromBundlePurchase]);

    const fetchVerticalShowData = async (showIdentifier) => {
        try {
            setLoading(true);
            unlockedEpisodesRef.current.clear();
            recordedTransactionsRef.current.clear(); 
            autoProcessedBundleRef.current.clear();
            const response = await service.getVerticalShowsList(appInfo, showIdentifier || paramShowId);
            if (response?.status === 200) {
                const responseData = response.data;
                setShowData(responseData.show);
                setEpisodes(responseData.data);
                setNextShowId(responseData.next_show_id);

                const liked = new Set();
                responseData?.data?.forEach(episode => {
                    if (episode.liked) {
                        liked.add(episode.video_id);
                    }
                });
                setLikedEpisodes(liked);

                let targetIndex = 0;
                
                    if (episodeId) {
                        const episodeIndex = responseData.data.findIndex(ep => {
                            return String(ep.video_id) === String(episodeId) || String(ep.vanity_url) === String(episodeId) || String(ep.share_url) === String(episodeId);
                        });
                        if (episodeIndex !== -1) {
                            targetIndex = episodeIndex;
                        }
                    } else if (responseData.resume_video_id) {
                        const resumeIndex = responseData.data.findIndex(ep => String(ep.vanity_url) === String(responseData.resume_video_id) || String(ep.video_id) === String(responseData.resume_video_id));
                        if (resumeIndex !== -1) {
                            targetIndex = resumeIndex;
                        }
                    } else {
                        targetIndex = 0;
                    }
                setCurrentEpisodeIndex(targetIndex);
            } else if (response?.status === 401) {
                navigate(projectInfo?.projectConfig?.config?.WEBSITE_NEW_CHECKOUT_REQUIRED === "true" ? "/checkout" : "/login", {
                    state: {
                        path: location?.pathname,
                        prevPath: location?.state?.prevPath,
                        activeLogin: true,
                        from: "VerticalShowPlayer"
                    }
                });
                return;
            }
            setLoading(false);
        } catch (error) {
            console.error("Error fetching vertical show data:", error);
            setLoading(false);
        }
    };
    const _fetchComments = async (page = 1, sort = sortType === "New" ? "Latest" : "Popular", prepend = false, Id, appInfo ) => {
        try {
          const params = {page, sort, video_id : Id, timestamp: new Date().getTime()};
          const response = await fetchComments(appInfo, params);
          if (response.data.success) {
            const page = response.data.page;
            setTotalComments(response.data.total);
           
          }
        } catch (error) {
          console.error("Error fetching comments:", error);
        }
    };

    const handleVideoStart = () => {
        if (!videoStarted && currentEpisode) {
            videoStarted = true;
            videoPlaying = true;
            if (debounce) {
                setTimeout(() => {
                    onVideoPlayFunction(
                        appInfo,
                        currentEpisode,
                        'POP12',
                        0
                    );
                }, 5000);
                debounce = false;
            }
        }
    };

    const handleVideoEnd = () => {
        videoPlaying = false;
        videoStarted = false;
        debounce = true;
        playNextEpisode();
    };

    const playNextEpisode = () => {
        if (currentEpisodeIndex < episodes.length - 1) {
            setCurrentEpisodeIndex(currentEpisodeIndex + 1);
            videoStarted = false;
            debounce = true;
        } else {
            if (nextShowId) {
                setShowData([]);
                setEpisodes([]);
                navigate(`/vertical-show/${nextShowId}`);
            } else {
                if (playerRef.current) {
                    playerRef.current.pause();
                }
            }
        }
    };

    const playPreviousEpisode = () => {
        if (currentEpisodeIndex > 0) {
            setCurrentEpisodeIndex(currentEpisodeIndex - 1);
            videoStarted = false;
            debounce = true;
        }
    };

    const closeBundlesModal = () => {
        setShowBundlesModal(false);
        setRequiredTokenId(null);
        setRequiredEpisodePrice(null);
    };

    const clearBundleResumeNavigationState = () => {
        if (!location?.state) {
            return;
        }

        const nextState = { ...location.state };
        delete nextState.resumeFromBundlePurchase;
        delete nextState.pendingConsumptionType;
        delete nextState.pendingVideoId;
        delete nextState.pendingShowId;

        const finalState = Object.keys(nextState).length > 0 ? nextState : null;
        navigate(location.pathname, { replace: true, state: finalState });
    };

    const selectEpisodeRange = (range) => {
        setCurrentEpisodeRange(range);
    };

    const getPaymentOptions = (episode) => {
        if (!episode || episode?.subscribed !== false) {
            return [];
        }

        return (episode?.subscriptions || []).filter((subscription) =>
            subscription?.type === 'bundle' || subscription?.type === 'subscription'
        );
    };

    const getVisiblePaymentOptions = (episode) => {
        const options = getPaymentOptions(episode);

        // DIRECT BALANCE CHECK
        const canAffordPerView = options.some((option) => {
            if (option?.type !== 'bundle' || option?.consumption_type !== 'PER_VIEW') {
                return false;
            }
            return Number(userCoins?.current_balance || 0) >= Number(option?.tokens_required || 0);
        });

        const canAffordAnyBundle = options.some((option) => {
            if (option?.type !== 'bundle') {
                return false;
            }
            return Number(userCoins?.current_balance || 0) >= Number(option?.tokens_required || 0);
        });

        if (!canAffordPerView && !canAffordAnyBundle) {
            return options;
        }

        return options.filter((option) => option?.type !== 'subscription');
    };

    const navigateToSubscriptionPlan = (episode) => {
        const targetRoute = projectInfo?.projectConfig?.config?.WEBSITE_NEW_CHECKOUT_REQUIRED === "true"
            ? "/checkout"
            : "/subscription";

        navigate(targetRoute, {
            state: {
                videoId: episode?.video_id,
                prevPath: location?.pathname,
                showId: paramShowId,
                from: "VerticalShowPlayer",
            },
        });
    };

    const handleCoinPayment = (episode, selectedOption) => {
        if (!episode || episode?.subscribed !== false) {
            return false;
        }

        if (!selectedOption || selectedOption?.type !== 'bundle') {
            return false;
        }

        const consumptionType = selectedOption?.consumption_type;

        if (unlockedEpisodesRef.current.has(episode.video_id) && consumptionType === 'UNLOCK') {
            return false;
        }

        const paymentOptions = getPaymentOptions(episode);

        const targetSubscription = paymentOptions.find((subscription) =>
            subscription?.type === 'bundle' &&
            subscription?.consumption_type === consumptionType &&
            String(subscription?.token_id) === String(selectedOption?.token_id)
        );

        if (!targetSubscription) {
            console.error('No subscription found for consumption type:', consumptionType);
            return false;
        }

        if (Number(userCoins?.current_balance || 0) < Number(targetSubscription?.tokens_required || 0)) {
            setRequiredTokenId(targetSubscription?.token_id);
            setRequiredEpisodePrice(Number(targetSubscription?.tokens_required || 0));
            localStorage.setItem("vertical_show_redirect", JSON.stringify({
                showId: paramShowId,
                videoId: episode.video_id,
                vanityUrl: `/vertical-show/${paramShowId}/${episode.vanity_url}`,
                consumptionType: consumptionType,
                source: "vertical-show-player",
                })
            );
            setShowBundlesModal(true);
            return false;
        }

        // Successful Payment - Deduct from Global Balance
        const updatedUserCoins = {
            ...(userCoins || {}),
            current_balance: Number(userCoins?.current_balance || 0) - Number(targetSubscription?.tokens_required || 0),
        };

        if (consumptionType === 'UNLOCK') {
            unlockedEpisodesRef.current.add(episode.video_id);
        }

        dispatch(
            getUserDetails({
                userDetails: {
                    ...userDetails,
                    user_coins: updatedUserCoins,
                },
            })
        );

        setEpisodes((prevEpisodes) =>
            prevEpisodes.map((ep) =>
                ep.video_id === episode.video_id
                    ? { ...ep, subscribed: true }
                    : ep
            )
        );

        recordCoinTransaction(episode.video_id, consumptionType);
        return true;
    };

    const handlePaymentOptionClick = (episode, option) => {
        if (!option || !episode) {
            return;
        }
        if (option.type === 'bundle') {
            handleCoinPayment(episode, option);
            return;
        }
        if (option.type === 'subscription') {
            navigateToSubscriptionPlan(episode);
        }
    };

    useEffect(() => {
        if (!location?.state?.resumeFromBundlePurchase || resumePaymentHandledRef.current) {
            return;
        }

        if (!currentEpisode) {
            return;
        }

        const pendingVideoId = location?.state?.pendingVideoId;
        const isMatchingEpisode =
            !pendingVideoId || String(currentEpisode?.video_id) === String(pendingVideoId);

        if (!isMatchingEpisode) {
            return;
        }

        if (currentEpisode?.subscribed !== false) {
            resumePaymentHandledRef.current = true;
            clearBundleResumeNavigationState();
            return;
        }

        const pendingConsumptionType = location?.state?.pendingConsumptionType;
        if (!pendingConsumptionType) {
            resumePaymentHandledRef.current = true;
            clearBundleResumeNavigationState();
            return;
        }

        const matchingOption = getPaymentOptions(currentEpisode).find((option) =>
            option?.type === 'bundle' && option?.consumption_type === pendingConsumptionType
        );

        if (!matchingOption) {
            resumePaymentHandledRef.current = true;
            clearBundleResumeNavigationState();
            return;
        }

        if (Number(userCoins?.current_balance || 0) >= Number(matchingOption?.tokens_required || 0)) {
            resumePaymentHandledRef.current = true;
            handleCoinPayment(currentEpisode, matchingOption);
            clearBundleResumeNavigationState();
        }
    }, [currentEpisode, location.state, userCoins]);

    const renderPaymentOptionLabel = (option) => {
        if (option?.type !== 'bundle') {
            return option?.button_name;
        }

        return (
            <>
                {option?.token_symbol && (
                    <img
                        src={option.token_symbol}
                        alt={option?.token_name || 'token'}
                        style={{ width: '18px', height: '18px', objectFit: 'contain', verticalAlign: 'middle' }}
                    />
                )}
                <span>{option?.tokens_required + " "} </span>
                <span>{option?.button_name}</span>
            </>
        );
    };

    const recordCoinTransaction = (videoId, consumptionType = 'UNLOCK') => {
        const transactionKey = `${videoId}_${consumptionType}`;
        if (recordedTransactionsRef.current.has(transactionKey)) {
            console.log(`[CoinUsage] Transaction already recorded for video ${videoId} with ${consumptionType}, skipping...`);
            return;
        }

        recordedTransactionsRef.current.add(transactionKey);
        console.log(`[CoinUsage] Initiating background transaction recording for video ${videoId} with ${consumptionType}`);

        service.recordCoinUsage(appInfo, videoId, consumptionType)
            .then((result) => {
                console.log(`[CoinUsage] Successfully recorded transaction for video ${videoId}:`, result?.message);
            })
            .catch((error) => {
                console.error(`[CoinUsage] Failed to record transaction for video ${videoId}:`, error);
                recordedTransactionsRef.current.delete(transactionKey);
            });
    };

    const selectEpisode = (index) => {
        if (index !== currentEpisodeIndex) {
            setCurrentEpisodeIndex(index);
            videoStarted = false;
            debounce = true;
        }
    };

    const formatDuration = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
    };

    const getCurrentVisibleEpisodes = () => {
        return episodes?.slice(currentEpisodeRange.start, currentEpisodeRange.end + 1) || [];
    };

    const toggleLike = async (episode) => {
        if (!user) {
            navigate(projectInfo?.projectConfig?.config?.WEBSITE_NEW_CHECKOUT_REQUIRED == "true" ? "/checkout" : "/login", {
                state: { path: location?.pathname, showId: paramShowId, prevPath: location?.state?.prevPath, activeLogin: true, from: "VerticalShowPlayer" },
            });
            return;
        }

        const newLikedStatus = !likedEpisodes.has(episode.video_id);

        try {
            const response = await service.likeHandler(episode.video_id, newLikedStatus, appInfo);

            if (response?.success) {
                const newLikedEpisodes = new Set(likedEpisodes);

                if (newLikedStatus) {
                    newLikedEpisodes.add(episode.video_id);
                } else {
                    newLikedEpisodes.delete(episode.video_id);
                }

                setLikedEpisodes(newLikedEpisodes);

                const updatedEpisodes = episodes.map(ep => {
                    if (ep.video_id === episode.video_id) {
                        return {
                            ...ep,
                            liked: newLikedStatus,
                            like_count: newLikedStatus ? ep.like_count + 1 : Math.max(0, ep.like_count - 1)
                        };
                    }
                    return ep;
                });

                setEpisodes(updatedEpisodes);
            }
        } catch (error) {
            console.error("Error toggling like for episode:", episode.video_id, error);
        }
    };

    const handleShareClick = async () => {
        const shareUrl = currentEpisode?.share_url || `${window.location.origin}${location?.pathname}`;
        try {
            await navigator.clipboard.writeText(shareUrl);
            toast.success(<Translate textKey={'copied'} />);
        } catch (error) {
            console.error('Failed to copy text:', error);
            toast.error('Failed to copy');
        }
    };

    if (loading) {
        return <Loading />;
    }

    const paymentOptions = currentEpisode ? getVisiblePaymentOptions(currentEpisode) : [];
    const shouldShowLockForCurrentEpisode = currentEpisode?.subscribed === false;
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 1024;
    if (isMobile) {
        return (
            <>
                {isCommentModalOpen && currentEpisode && (
                    <div className="vertical-comments">
                        <div className="comment-modal">
                            <Comments_container
                                videoId={currentEpisode.video_id}
                                projectInfo={projectInfo}
                                setCommentModalOpen={(val) => { setCommentModalOpen(val); if (!val) setShouldCloseCommentModal(true); }}
                                setCommentCount={setCommentCount}
                            />
                        </div>
                    </div>
                )}
                
                {/* Bundles Modal */}
                {showBundlesModal && (
                    <div className="bundles-modal-overlay" onClick={closeBundlesModal}>
                        <div className="bundles-modal-content" onClick={(e) => e.stopPropagation()}>
                            <BundlesList 
                                tokenId={requiredTokenId}
                                hideTransactionHistory={true}
                                isPopupMode={true}
                                episodePrice={requiredEpisodePrice}
                                onClose={closeBundlesModal}
                                onBundleSelect={() => {
                                    closeBundlesModal();
                                }}
                            />
                        </div>
                    </div>
                )}
                <div className="verticalShowPlayer">
                    <ToastContainer />
                    <div className="playerContainer">
                        <div className="leftPanel">
                            <div className="videoPlayer">
                                {currentEpisode && (
                                    shouldShowLockForCurrentEpisode ? (
                                    <div className="video-container lockedBackground">
                                        <img
                                            className="lockedBackgroundImage"
                                            src={currentEpisode.thumbnail || showData?.logo || ""}
                                            alt={showData?.show_name || currentEpisode.title}
                                        />

                                        <div className="lockedOverlay">
                                            <svg fill="#f0f0f0" width="64px" height="64px" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg" stroke="#f0f0f0"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"><path d="M800 384h-32V261.872C768 115.024 661.744 0 510.816 0 359.28 0 256 117.472 256 261.872V384h-32c-70.592 0-128 57.408-128 128v384c0 70.592 57.408 128 128 128h576c70.592 0 128-57.408 128-128V512c0-70.592-57.408-128-128-128zM320 261.872C320 152.784 394.56 64 510.816 64 625.872 64 704 150.912 704 261.872V384H320V261.872zM864.001 896c0 35.28-28.72 64-64 64h-576c-35.28 0-64-28.72-64-64V512c0-35.28 28.72-64 64-64h576c35.28 0 64 28.72 64 64v384zm-352-320c-35.344 0-64 28.656-64 64 0 23.632 12.96 44.032 32 55.12V800c0 17.664 14.336 32 32 32s32-14.336 32-32V695.12c19.04-11.088 32-31.504 32-55.12 0-35.344-28.656-64-64-64z"></path></g></svg>
                                            <div className="lockedTextContainer">
                                                <p className="lockedText"><Translate textKey={"no_subscription_to_play"} /></p>
                                                {paymentOptions.length > 0 && 
                                                        paymentOptions.map((option, index) => (
                                                            <button
                                                                key={index}
                                                                className={`lockedButton`}
                                                                onClick={() => handlePaymentOptionClick(currentEpisode, option)}
                                                            >
                                                                {renderPaymentOptionLabel(option)}
                                                            </button>
                                                        ))
                                                 }

                                                
                                            </div>
                                        </div>
                                    </div>
                                    ) : (
                                        <>
                                            <Videos
                                                key={currentEpisode.video_id}
                                                id={`episode-${currentEpisode.video_id}`}
                                                originalId={currentEpisode.video_id}
                                                src={currentEpisode?.url}
                                                subtitles={currentEpisode.subtitles || []}
                                                like={currentEpisode.like_count}
                                                comment={currentEpisode.comment_count || 0}
                                                shareUrl=""
                                                title=""
                                                desc=""
                                                projectInfo={projectInfo}
                                                video_time={currentEpisode.video_time}
                                                isVisible={true}
                                                isInitial={true}
                                                appInfo={appInfo}
                                                vanityUrl={currentEpisode.vanity_url}
                                                liked_flag={null}
                                                poster={currentEpisode.thumbnail}
                                                handleScroll={() => { }}
                                                ensureNextFetch={() => { }}
                                                shouldCloseCommentModal={shouldCloseCommentModal}
                                                setShouldCloseCommentModal={setShouldCloseCommentModal}
                                                onVideoEnd={handleVideoEnd}
                                                onVideoStart={handleVideoStart}
                                                showControls={true}
                                                hideAllOverlays={true}
                                            />
                                        </>
                                    )
                                )}
                            </div>
                        </div>
                        <div className="rightPanel">
                            <div className="showInfo">
                                {showData && currentEpisode && (
                                    <div className="showTitleRow">
                                        <span
                                            className="showTitle"
                                            onClick={() => navigate(`/show-details/${paramShowId}`)}
                                        >
                                            {showData.show_name}
                                        </span>
                                        <span className="slashSeparator">/</span>
                                        <span className="episodeTitleText">{currentEpisode.episode_text}</span>
                                    </div>
                                )}
                            </div>
                            <div className="episodesList">
                                <div className="episodeRanges">
                                    {episodeRanges.map((range, index) => {
                                        const isActive = range.start === currentEpisodeRange.start;
                                        const label = range.start === range.end ?
                                            `${range.start + 1}` :
                                            `${range.start + 1}-${range.end + 1}`;
                                        return (
                                            <button
                                                key={index}
                                                className={`rangeButton ${isActive ? 'active' : ''}`}
                                                onClick={() => selectEpisodeRange(range)}
                                            >
                                                {label}
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="episodesContainer">
                                    <div className="episodesGrid">
                                        {getCurrentVisibleEpisodes().map((episode, index) => {
                                            const actualIndex = currentEpisodeRange.start + index;
                                            const subscribedLock = episode.subscribed === false;
                                            return (
                                                <div
                                                    key={episode.video_id}
                                                    className={`episodeItem ${actualIndex === currentEpisodeIndex ? 'active' : ''}`}
                                                    onClick={() => selectEpisode(actualIndex)}
                                                >
                                                    {subscribedLock && (
                                                        <div className="episodeLock">
                                                            <img src={LockIcon} alt="locked" />
                                                        </div>
                                                    )}
                                                    {actualIndex === currentEpisodeIndex ? <PlayingIcon /> : episode?.title}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                {currentEpisode && (
                                    <div className="currentEpisodeInfo mobile">
                                        <div className="metadata">
                                            {currentEpisode.description ? (
                                                <p className="episodeDescription">{currentEpisode.description}</p>
                                            ) : showData?.synopsis && (
                                                <p className="episodeDescription">
                                                    {showData.synopsis.length > 150
                                                        ? `${showData.synopsis.substring(0, 150)}...`
                                                        : showData.synopsis
                                                    }
                                                    {showData.synopsis.length > 150 && (
                                                        <button
                                                            className="moreBtn"
                                                            onClick={() => navigate(`/show-details/${paramShowId}`)}
                                                        >
                                                            <Translate textKey={'more'} />
                                                        </button>
                                                    )}
                                                </p>
                                            )}
                                        </div>
                                        <div className="episodeButtonsRow">
                                            <button
                                                className={`likeBtn ${likedEpisodes.has(currentEpisode.video_id) ? 'liked' : ''}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    toggleLike(currentEpisode);
                                                }}
                                            >
                                                {likedEpisodes.has(currentEpisode.video_id) ? <HeartFillIcon /> : <HeartIcon />}
                                                <span>{currentEpisode.like_count}</span>
                                            </button>
                                            {projectInfo?.projectConfig?.config?.COMMENTS_REQUIRED === "true" &&
                                                <button
                                                    className="actionBtn commentBtn"
                                                    onClick={() => {
                                                        if (!user) {
                                                            navigate(projectInfo?.projectConfig?.config?.WEBSITE_NEW_CHECKOUT_REQUIRED == "true" ? "/checkout" : "/login", {
                                                                state: { path: location?.pathname, showId: paramShowId, prevPath: location?.state?.prevPath, activeLogin: true, from: "VerticalShowPlayer" },
                                                            });
                                                            return;
                                                        }
                                                        setCommentModalOpen(true);
                                                        setShouldCloseCommentModal(false);
                                                    }}
                                                >
                                                    <InsertCommentIcon />
                                                    <span>{totalComments}</span>
                                                </button>
                                            }
                                            <button className="actionBtn shareBtn" onClick={handleShareClick}>
                                                <ShareIcon />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            {isCommentModalOpen && currentEpisode && (
                <div className="vertical-comments">
                    <div className="comment-modal">
                        <Comments_container
                            videoId={currentEpisode.video_id}
                            projectInfo={projectInfo}
                            setCommentModalOpen={(val) => { setCommentModalOpen(val); if (!val) setShouldCloseCommentModal(true); }}
                            setCommentCount={setCommentCount}
                        />
                    </div>
                </div>
            )}
            <div className="verticalShowPlayer">
                <ToastContainer />
                <div className="playerContainer">
                    <div className="leftPanel">
                        <div className="videoPlayer">
                            {currentEpisode && (
                                shouldShowLockForCurrentEpisode ? (
                                    <div className="video-container lockedBackground">
                                        <img
                                            className="lockedBackgroundImage"
                                            src={currentEpisode.thumbnail || showData?.logo || ""}
                                            alt={showData?.show_name || currentEpisode.title}
                                        />

                                        <div className="lockedOverlay">
                                            <svg fill="#f0f0f0" width="64px" height="64px" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg" stroke="#f0f0f0"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"><path d="M800 384h-32V261.872C768 115.024 661.744 0 510.816 0 359.28 0 256 117.472 256 261.872V384h-32c-70.592 0-128 57.408-128 128v384c0 70.592 57.408 128 128 128h576c70.592 0 128-57.408 128-128V512c0-70.592-57.408-128-128-128zM320 261.872C320 152.784 394.56 64 510.816 64 625.872 64 704 150.912 704 261.872V384H320V261.872zM864.001 896c0 35.28-28.72 64-64 64h-576c-35.28 0-64-28.72-64-64V512c0-35.28 28.72-64 64-64h576c35.28 0 64 28.72 64 64v384zm-352-320c-35.344 0-64 28.656-64 64 0 23.632 12.96 44.032 32 55.12V800c0 17.664 14.336 32 32 32s32-14.336 32-32V695.12c19.04-11.088 32-31.504 32-55.12 0-35.344-28.656-64-64-64z"></path></g></svg>
                                            <div className="lockedTextContainer">
                                                <p className="lockedText"><Translate textKey={"no_subscription_to_play"} /></p>
                                                {paymentOptions.length > 0 && 
                                                        paymentOptions.map((option, index) => (
                                                            <button
                                                                key={index}
                                                                className={`lockedButton`}
                                                                onClick={() => handlePaymentOptionClick(currentEpisode, option)}
                                                            >
                                                                {renderPaymentOptionLabel(option)}
                                                            </button>
                                                        ))
                                                 }
                                                
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <Videos
                                            key={currentEpisode.video_id}
                                            id={`episode-${currentEpisode.video_id}`}
                                            originalId={currentEpisode.video_id}
                                            src={currentEpisode?.url}
                                            subtitles={currentEpisode.subtitles || []}
                                            like={currentEpisode.like_count}
                                            comment={currentEpisode.comment_count || 0}
                                            shareUrl=""
                                            title=""
                                            desc=""
                                            projectInfo={projectInfo}
                                            video_time={currentEpisode.video_time}
                                            isVisible={true}
                                            isInitial={true}
                                            appInfo={appInfo}
                                            vanityUrl={currentEpisode.vanity_url}
                                            liked_flag={null}
                                            poster={currentEpisode.thumbnail}
                                            handleScroll={() => { }}
                                            ensureNextFetch={() => { }}
                                            shouldCloseCommentModal={shouldCloseCommentModal}
                                            setShouldCloseCommentModal={setShouldCloseCommentModal}
                                            onVideoEnd={handleVideoEnd}
                                            onVideoStart={handleVideoStart}
                                            showControls={true}
                                            hideAllOverlays={true}
                                        />

                                    </>
                                )
                            )}
                        </div>
                    </div>

                    <div className="rightPanel">
                        <div className="showInfo">
                            {showData && (
                                <h1
                                    className="showTitle"
                                    onClick={() => navigate(`/show-details/${paramShowId}`)}
                                >
                                    {showData.show_name}
                                </h1>
                            )}
                        </div>

                        {currentEpisode && (
                            <div className="currentEpisodeInfo">
                                <div className="metadata">
                                    <h2 className="episodeTitle">
                                        {/* {currentEpisode?.title === "Trailer" ? currentEpisode?.title : <><Translate textKey="episode" /> {currentEpisode.episode_number}</>} */}
                                        {currentEpisode?.episode_text}
                                    </h2>
                                    {currentEpisode.description ? (
                                        <p className="episodeDescription">{currentEpisode.description}</p>
                                    ) : showData?.synopsis && (
                                        <p className="episodeDescription">
                                            {showData.synopsis.length > 150 
                                                ? `${showData.synopsis.substring(0, 150)}...` 
                                                : showData.synopsis
                                            }
                                            {showData.synopsis.length > 150 && (
                                                <button 
                                                    className="moreBtn"
                                                    onClick={() => navigate(`/show-details/${paramShowId}`)}
                                                >
                                                    <Translate textKey={'more'} />
                                                </button>
                                            )}
                                        </p>
                                    )}
                                </div>
                                <div className="episodeButtonsRow">
                                    <button
                                        className={`likeBtn ${likedEpisodes.has(currentEpisode.video_id) ? 'liked' : ''}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            toggleLike(currentEpisode);
                                        }}
                                    >
                                        {likedEpisodes.has(currentEpisode.video_id) ? <HeartFillIcon /> : <HeartIcon />}
                                        <span>{currentEpisode.like_count}</span>
                                    </button>

                                    {projectInfo?.projectConfig?.config?.COMMENTS_REQUIRED === "true" && 
                                        <button
                                            className="actionBtn commentBtn"
                                            onClick={() => {
                                                if (!user) {
                                                    navigate(projectInfo?.projectConfig?.config?.WEBSITE_NEW_CHECKOUT_REQUIRED == "true" ? "/checkout" : "/login", {
                                                        state: { path: location?.pathname, showId: paramShowId, prevPath: location?.state?.prevPath, activeLogin: true, from: "VerticalShowPlayer" },
                                                    });
                                                    return;
                                                }
                                                setCommentModalOpen(true);
                                                setShouldCloseCommentModal(false);
                                            }}
                                        >
                                            <InsertCommentIcon />
                                            <span>{totalComments}</span>
                                        </button>
                                    }

                                    <button className="actionBtn shareBtn" onClick={handleShareClick}>
                                        <ShareIcon />
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className="episodesList">
                            <div className="episodeRanges">
                                {episodeRanges.map((range, index) => {
                                    const isActive = range.start === currentEpisodeRange.start;
                                    const label = range.start === range.end ?
                                        `${range.start + 1}` :
                                        `${range.start + 1}-${range.end + 1}`;
                                    return (
                                        <button
                                            key={index}
                                            className={`rangeButton ${isActive ? 'active' : ''}`}
                                            onClick={() => selectEpisodeRange(range)}
                                        >
                                            {label}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="episodesContainer">
                                <div className="episodesGrid">
                                    {getCurrentVisibleEpisodes().map((episode, index) => {
                                        const actualIndex = currentEpisodeRange.start + index;
                                        const subscribedLock = episode.subscribed === false;
                                        return (
                                            <div
                                                key={episode.video_id}
                                                className={`episodeItem ${actualIndex === currentEpisodeIndex ? 'active' : ''}`}
                                                onClick={() => selectEpisode(actualIndex)}
                                            >
                                                {subscribedLock && (
                                                    <div className="episodeLock">
                                                        <img src={LockIcon} alt="locked" />
                                                    </div>
                                                )}
                                                {actualIndex === currentEpisodeIndex ? <PlayingIcon /> : episode?.title}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Bundles Modal */}
            {showBundlesModal && (
                <div className="bundles-modal-overlay" onClick={closeBundlesModal}>
                    <div className="bundles-modal-content" onClick={(e) => e.stopPropagation()}>
                        <BundlesList 
                            tokenId={requiredTokenId}
                            hideTransactionHistory={true}
                            isPopupMode={true}
                            episodePrice={requiredEpisodePrice}
                            onClose={closeBundlesModal}
                            onBundleSelect={(bundle) => {
                                closeBundlesModal();
                            }}
                        />
                    </div>
                </div>
            )}
        </>
    );
};

export default VerticalShowPlayer;